import { useState, useEffect, useRef, useMemo } from 'react';
import qs from 'query-string';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import { Geocoder, debounceEventHandler } from '../tools';
import * as API from "../api";
import { paramsFromUrl, generateSearchUrl, generateItemUrl } from '../UrlParams';
import { MapMapbox } from "../components/Map";
import Filters, { LocationFilter } from "../components/Filters";
import ItemsTools from "../components/item/ItemsTools";
import { ItemHeader, ItemDescription, ItemFooter } from "../components/item/Item";
import NotFound from "./NotFound";
import { ItemMetaInfo } from "../MetaInfo";
import { Button } from "@/components/ui/button";


function SearchItemView({ item, params }) {
    const back = generateSearchUrl(params);
    return (
        <div className="[grid-area:results] z-[3] p-2.5 bg-[#ededf1] flex flex-col min-h-0 [&_.ItemDescription]:h-0 max-sm:fixed max-sm:top-0 max-sm:left-0 max-sm:w-full max-sm:h-full max-sm:z-[100]">
            <ItemMetaInfo item={item} />
            <ItemHeader item={item} back={back} className="shrink-0" />
            <ItemDescription item={item} />
            <ItemFooter item={item} />
        </div>
    );
}

function getParamsFromMap(map) {
    const _bounds = map.getBounds();
    const center = map.getCenter();
    const zoom = map.getZoom();
    const bounds = {
        bottom_left: [_bounds.getWest(), _bounds.getSouth()],
        top_right: [_bounds.getEast(), _bounds.getNorth()]
    };
    return { center, bounds, zoom };
}


export default function SearchView() {
    const navigate = useNavigate();
    const urlParams = useParams();
    const location = useLocation();

    const initialParams = useMemo(() => paramsFromUrl({
        ...urlParams,
        ...qs.parse(location.search)
    }), []); // eslint-disable-line react-hooks/exhaustive-deps

    const [params, setParams] = useState(initialParams);
    const [mapCenter, setMapCenter] = useState({ ...initialParams.center });
    const [mapZoom, setMapZoom] = useState(initialParams.zoom);
    const [mapClusters, setMapClusters] = useState([]);
    const [item, setItem] = useState(null);
    const [itemFound, setItemFound] = useState(null);
    const [total, setTotal] = useState(null);
    const [items, setItems] = useState([]);
    const [scroll, setScroll] = useState(null);
    const [loading, setLoading] = useState(false);
    const [zIndex, setZIndex] = useState({ map: 2, list: 1 });

    const listRef = useRef(null);

    // Refs for stable access in debounced/async callbacks
    const latestRef = useRef({});
    latestRef.current = { params, item, items, total, loading, navigate };

    // Load item if URL has an id
    useEffect(() => {
        if (!initialParams.id) return;
        API.itemQuery(initialParams.id).then(({ found, item }) => {
            if (!found) {
                setItemFound(false);
            } else {
                const loc = item.locations[0].location;
                const center = { lng: loc.lon, lat: loc.lat };
                setItem(item);
                setItemFound(true);
                setMapZoom(13);
                setMapCenter(center);
                setParams(prev => ({ ...prev, center, zoom: 13 }));
            }
        }).catch(() => setItemFound(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Clear item when navigating away from item URL
    useEffect(() => {
        if (!urlParams.id) setItem(null);
    }, [urlParams.id]);

    const resetScroll = () => {
        if (listRef.current) listRef.current.scrollTop = 0;
    };

    const haveItemsChanged = (nextItems) => {
        const { items: currentItems } = latestRef.current;
        if (nextItems.length < currentItems.length) return true;
        for (let i = 0; i < currentItems.length; i++) {
            if (currentItems[i].id !== nextItems[i].id) return true;
        }
        return false;
    };

    const onItemsScroll = (e) => {
        const { items: currentItems, total: currentTotal, loading: currentLoading } = latestRef.current;
        if (currentItems.length === currentTotal || currentLoading) return;

        const body = e.currentTarget;
        const positionMax = body.scrollHeight - body.clientHeight;
        const position = body.scrollTop;

        if (positionMax - position < 90 * 5) {
            setLoading(true);
            const searchAfter = currentItems[currentItems.length - 1];
            API.listQuery(params, searchAfter).then(({ items: moreItems }) => {
                setItems(prev => [...prev, ...moreItems]);
                setLoading(false);
            });
        }
    };

    const onFilterChange = (newParams) => {
        API.listQuery(newParams).then(({ scroll: s, items: newItems, total: t }) => {
            if (haveItemsChanged(newItems)) resetScroll();
            setItems(newItems);
            setScroll(s);
            setTotal(t);
            setLoading(false);
        });
        API.clusterQuery(newParams).then(setMapClusters);

        const url = generateSearchUrl(newParams);
        if (latestRef.current.item) navigate(url);
        else navigate(url, { replace: true });

        setParams(newParams);
    };

    const onFilterLocationChange = useMemo(
        () => debounceEventHandler(e => {
            const { params: p, item: it, navigate: nav } = latestRef.current;
            const newParams = { ...p, location: e.target.value };
            setParams(newParams);
            const url = generateSearchUrl(newParams);
            Geocoder.get(e.target.value)
                .then(({ center, zoom }) => {
                    setMapCenter(center);
                    setMapZoom(zoom);
                })
                .finally(() => {
                    if (it) nav(url);
                    else nav(url, { replace: true });
                });
        }, 400),
        [] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const onMapReady = (map) => {
        const { bounds, center, zoom } = getParamsFromMap(map);
        const newParams = { ...initialParams, bounds, zoom, center };
        Promise.all([
            API.clusterQuery(newParams),
            API.listQuery(newParams)
        ]).then(([clusters, { scroll: s, items: newItems, total: t }]) => {
            setMapClusters(clusters);
            setItems(newItems);
            setScroll(s);
            setTotal(t);
            setLoading(false);
        });
        setParams(newParams);
    };

    const onMapBoundChange = (map) => {
        const { bounds, center, zoom } = getParamsFromMap(map);
        const { params: p, item: it } = latestRef.current;
        const newParams = { ...p, bounds, zoom, center };

        API.clusterQuery(newParams).then(setMapClusters);
        API.listQuery(newParams).then(({ scroll: s, items: newItems, total: t }) => {
            if (haveItemsChanged(newItems)) resetScroll();
            setItems(newItems);
            setScroll(s);
            setTotal(t);
            setLoading(false);
        });

        if (it) {
            const contains = it.locations.some(l => {
                const bounds = map.getBounds();
                const lng = (l.location.lon - bounds._ne.lng) * (l.location.lon - bounds._sw.lng) < 0;
                const lat = (l.location.lat - bounds._ne.lat) * (l.location.lat - bounds._sw.lat) < 0;
                return lat && lng;
            });
            if (!contains) {
                latestRef.current.navigate(generateSearchUrl(newParams));
                setItem(null);
            }
        } else {
            latestRef.current.navigate(generateSearchUrl(newParams), { replace: true });
        }

        setParams(newParams);
    };

    const onMapClusterClick = (map, e) => {
        const coordinates = e.features[0].geometry.coordinates.slice();
        map.flyTo({ center: coordinates, zoom: map.getZoom() + 3, speed: 3.5 });
    };

    if (urlParams.id && !item) {
        if (itemFound === false) return <NotFound />;
        return null;
    }

    return (
        <div className="search-view-grid">

            {/* Filter bar */}
            <div className="[grid-area:filters]">
                <Filters params={params} onChange={onFilterChange} className="w-full" />
            </div>

            {/* Map */}
            {(mapZoom && mapCenter) &&
                <MapMapbox
                    zoom={mapZoom}
                    center={mapCenter}
                    disableDoubleClickZoom={false}
                    clusters={mapClusters}
                    marker={item ? [
                        item.locations[0].location.lon,
                        item.locations[0].location.lat
                    ] : null}
                    navigationControlOptions={{ showZoom: true, showCompass: false }}
                    clustersOpacity={item ? 0.3 : 1}
                    onClusterClick={onMapClusterClick}
                    onBoundChange={onMapBoundChange}
                    onMapReady={onMapReady}
                    className="[grid-area:map] z-[2] max-sm:[grid-area:results]"
                >
                    {/* Inset shadow overlay */}
                    <div className="absolute inset-0 z-[1] pointer-events-none shadow-[inset_5px_5px_20px_-10px_rgba(0,0,0,0.75)]" />

                    {/* Location filter on top of map */}
                    <div className="absolute top-0 left-0 w-full z-[1] flex p-5 pointer-events-none">
                        <LocationFilter
                            value={params.location}
                            onChange={onFilterLocationChange}
                            className="pointer-events-auto flex-1 shadow-[0_4px_8px_0_rgba(0,0,0,0.15)] [&_input]:h-[50px]" />
                    </div>

                    {/* Mobile: tap to see job list */}
                    <div className="hidden max-sm:block fixed bottom-10 left-0 w-full z-[1] p-5 pointer-events-none">
                        <Button
                            onClick={() => setZIndex({ map: 1, list: 2 })}
                            className="pointer-events-auto shadow-[0_4px_8px_0_rgba(0,0,0,0.15)] bg-green hover:bg-green/90 text-white">
                            See Jobs
                        </Button>
                    </div>
                </MapMapbox>}

            {/* Job list */}
            <div className="[grid-area:results] flex flex-col" style={{ zIndex: zIndex.list }}>
                <ItemsTools params={params} total={total} onChange={onFilterChange} />

                <div className="overflow-y-auto h-0 flex-1 px-2.5 bg-[#f0f0f0]" onScroll={onItemsScroll} ref={listRef}>
                    <div className="h-0">
                        {items.map(item => (
                            <ItemHeader key={item.id}
                                className="mb-2.5 cursor-pointer shadow-[0_1px_1px_0_rgba(0,0,0,0.2)]"
                                onClick={setItem}
                                to={{ ...generateItemUrl(item), state: { item } }}
                                item={item} />
                        ))}

                        <div className="h-[100px] flex flex-col justify-center items-center">
                            {loading && <i className="fa fa-spinner fa-pulse fa-fw text-[#a7a6ad] text-[22px]" />}
                            {!loading && items.length === total && <div className="text-[#a7a6ad] text-[22px] font-light">{"That's it!"}</div>}
                            {!loading && items.length === 0 && <div className="text-[#a7a6ad] text-[22px] font-light">Nothing to do here</div>}
                        </div>
                    </div>
                </div>

                {/* Mobile: tap to show map */}
                <div className="hidden max-sm:block fixed bottom-10 left-0 w-full z-[1] p-5 pointer-events-none">
                    <Button
                        onClick={() => setZIndex({ map: 2, list: 1 })}
                        className="pointer-events-auto shadow-[0_4px_8px_0_rgba(0,0,0,0.15)] bg-orange hover:bg-orange/90 text-white">
                        Change Location
                    </Button>
                </div>
            </div>

            {/* Item detail panel */}
            {item && <SearchItemView item={item} params={params} />}
        </div>
    );
}
